import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  makeStyles,
  withStyles,
  Card,
  CardContent,
  IconButton,
  Icon,
} from "@material-ui/core";
import { NavLink } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Clear";
import SaveIcon from "@material-ui/icons/Check";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { updateEntity } from "./../../../../API/Entity";
import AttributeGroup from "./../../../../Component/Forms/AttributeGroup";
import { getAttribute } from "../../../../Utilities/Functions";
import styles from "./styles";

const attributeIds = [
  "3", //Full Name
  "11", //Company Reg. No.
  "8", //Legal status
  "80", //National of
  "67", //Gender
  "68", //Job title(s)
  "69", //DOB
  "84", //Name at Birth
  "86", //Place Of Birth
  "85", //Mother's Maiden name
  "72", //Legal status (complex)
  "14", //Ownership type
  "30", //Public authority
  "18", //Listing status
  "22", //Primary Exchange
  "73", //Equivalence (Listing)
  "26", //Regulated status (finan.)
  "27", //Name of regulator
  "74", //Equivalence (Regulated)
];

const RecordInfo = (props) => {
  const { entity, listOptions = [], onAttributeChange, onEntitySave } = props;
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const { showErrorMessage } = useContext(ModalDialogContext);

  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [attributeChanges, setAttributeChanges] = useState(null);

  useEffect(() => {
    const { attributes = [] } = entity || {};
    setAttributes(
      attributes
        .filter((a) => attributeIds.indexOf(a.id) > -1)
        .sort((a, b) => {
          const idx1 = attributeIds.indexOf(a.id);
          const idx2 = attributeIds.indexOf(b.id);
          return idx1 === idx2 ? 0 : idx1 > idx2 ? 1 : -1;
        })
        .map((a) => ({
          ...a,
          editable: a.id === "74" || a.id === "73" ? false : editMode,
        })),
    );
  }, [editMode, entity]);

  const handleAttributeChange = (attrId, newValue) => {
    const changes = {};
    const attr = attributes.find((a) => a.id === attrId);
    if (attr) {
      changes[attrId] = newValue;
    }

    if (attrId === "18") {
      if (newValue !== "Yes(Client)" && newValue !== "Yes(Ultimate Parent)") {
        changes["73"] = "NA";
        changes["22"] = "";
      }
    }
    if (attrId === "22") {
      if (newValue) {
        changes["18"] = "Yes(Client)";
        const exchangeAttr = getAttribute(attributes, "22");
        const options = (listOptions || {})[
          exchangeAttr.list_id || exchangeAttr.data_type
        ];
        if (options) {
          const exchange = options.find((e) => e.value === newValue);
          changes["73"] = exchange && exchange.equivalent ? "Yes" : "No";
        }
      } else {
        changes["73"] = "NA";
      }
    }

    if (attrId === "26") {
      if (newValue !== "Yes") {
        changes["74"] = "NA";
        changes["27"] = "";
      }
    }
    if (attrId === "27") {
      if (newValue) {
        changes["26"] = "Yes";
        const regulatorAttr = getAttribute(attributes, "27");
        const options = (listOptions || {})[
          regulatorAttr.list_id || regulatorAttr.data_type
        ];
        if (options) {
          const regulator = options.find((e) => e.value === newValue);
          changes["74"] = regulator && regulator.equivalent ? "Yes" : "No";
        }
      } else {
        changes["74"] = "NA";
      }
    }

    setAttributes(
      attributes
        .filter((a) => attributeIds.indexOf(a.id) > -1)
        .sort((a, b) => {
          const idx1 = attributeIds.indexOf(a.id);
          const idx2 = attributeIds.indexOf(b.id);
          return idx1 === idx2 ? 0 : idx1 > idx2 ? 1 : -1;
        })
        .map((a) => {
          const user_value =
            changes[a.id] === undefined ? a.user_value : changes[a.id];
          return {
            ...a,
            user_value,
            editable: a.id === "74" || a.id === "73" ? false : editMode,
          };
        }),
    );
    setAttributeChanges(changes);
    onAttributeChange(changes);
  };

  const saveChanges = async () => {
    if (!attributeChanges) {
      return false;
    }
    try {
      setSaving(true);
      const { id } = entity;
      const changedAttributes = attributes
        .filter((a) => attributeChanges[a.id] !== undefined)
        .map((a) => {
          return {
            newValue: a.user_value,
            id: a.id,
            newUnit: a.user_unit,
          };
        });
      entity.attributes = attributes;
      await updateEntity({ entityId: id, attributes: changedAttributes });
      setAttributeChanges(null);
      setEditMode(false);
      onEntitySave();
      setSaving(false);
      return true;
    } catch (error) {
      showErrorMessage({ error });
      setSaving(false);
      return false;
    }
  };

  const edit = () => {
    setEditMode(true);
  };

  const cancelEdit = () => {
    setAttributeChanges(null);
    onAttributeChange(null);
    setEditMode(false);
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={11}>
        <Card variant="outlined">
          <CardContent>
            {saving ? (
              renderLoadingIndicator()
            ) : (
              <AttributeGroup
                attributes={attributes}
                onChange={handleAttributeChange}
                listOptions={listOptions}
                spacing={1}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={1}>
        {!editMode && (
          <IconButton onClick={edit} className={classes.actionIcons}>
            <EditIcon />
          </IconButton>
        )}
        {!editMode && (
          <NavLink to={`/basicinfo?id=${entity.id}`} target="_blank">
            <IconButton className={classes.actionIcons}>
              <Icon
                className="far fa-arrow-circle-right"
                style={{ fontSize: 16 }}
              />
            </IconButton>
          </NavLink>
        )}
        {editMode && (
          <IconButton onClick={saveChanges} className={classes.actionIcons}>
            <SaveIcon />
          </IconButton>
        )}
        {editMode && (
          <IconButton onClick={cancelEdit} className={classes.actionIcons}>
            <CancelIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(RecordInfo);
